import { ApolloError, gql, useMutation } from '@apollo/client';
import { useFormik } from 'formik';
import _ from 'lodash';
import React, { useCallback, useState } from 'react';
import * as Yup from 'yup';

import { ErrorMessage } from 'src/components/errorMessage/ErrorMessage';
import { useDebouncedEffect } from 'src/hooks/useDebouncedEffect';
import { GraphQLTypes } from 'src/lib/graphqlTypes';

import { AUTOSAVE_TIMEOUT_MS } from '../../checksConfigPage/constants';
import { IgnoredConditionsCard } from '../../checksConfigPage/ignoredConditionsCard/IgnoredConditionsCard';
import { SharedCheckConfigCardProps } from '../common/SharedCheckConfigCardProps';
import { VariantCheckConfigFragment } from '../VariantCheckConfigFragment';

const variantCheckConfigDowngradeChecksMutation = gql`
  mutation VariantCheckConfigDowngradeChecksMutation(
    $graphId: ID!
    $graphVariant: String!
    $useGraphSettings: Boolean!
    $downgradeStaticChecks: Boolean
    $downgradeDefaultValueChange: Boolean
  ) {
    service(id: $graphId) {
      variant(name: $graphVariant) {
        updateCheckConfigurationDowngradeChecks(
          useGraphSettings: $useGraphSettings
          downgradeStaticChecks: $downgradeStaticChecks
          downgradeDefaultValueChange: $downgradeDefaultValueChange
        ) {
          id
          ...VariantCheckConfigFragment
        }
      }
    }
  }
  ${VariantCheckConfigFragment}
`;

/**
 * Schema for the form
 */
const validationSchema = Yup.object().shape({
  useGraphSettings: Yup.boolean().required(),
  downgradeStaticChecks: Yup.boolean().required().nullable(),
  downgradeDefaultValueChange: Yup.boolean().required().nullable(),
});

/**
 * Types inferred from `validationSchema`
 */
type FormValues = Yup.InferType<typeof validationSchema>;

type Props = SharedCheckConfigCardProps;

export const DowngradeChecksCard = ({
  graphRef,
  graphCheckConfig,
  variantCheckConfig,
  roles,
}: Props) => {
  const [updateConfigError, setUpdateConfigError] =
    useState<ApolloError | null>(null);

  const [updateCheckConfig] = useMutation<
    GraphQLTypes.VariantCheckConfigDowngradeChecksMutation,
    GraphQLTypes.VariantCheckConfigDowngradeChecksMutationVariables
  >(variantCheckConfigDowngradeChecksMutation, {
    onError: setUpdateConfigError,
  });

  const variantDowngradeChecksConfig = variantCheckConfig.downgradeChecksConfig;

  const { initialValues, values, dirty, errors, setFieldValue, submitForm } =
    useFormik<FormValues>({
      enableReinitialize: true,
      initialValues: {
        useGraphSettings: variantDowngradeChecksConfig.useGraphSettings,
        downgradeStaticChecks: variantDowngradeChecksConfig.useGraphSettings
          ? graphCheckConfig.downgradeStaticChecks
          : variantDowngradeChecksConfig.downgradeStaticChecks,
        downgradeDefaultValueChange:
          variantDowngradeChecksConfig.useGraphSettings
            ? graphCheckConfig.downgradeDefaultValueChange
            : variantDowngradeChecksConfig.downgradeDefaultValueChange,
      },
      onSubmit: async (formValues) => {
        await updateCheckConfig({
          variables: {
            graphId: graphRef.graphId,
            graphVariant: graphRef.graphVariant,
            useGraphSettings: formValues.useGraphSettings,
            downgradeStaticChecks: formValues.useGraphSettings
              ? null
              : formValues.downgradeStaticChecks,
            downgradeDefaultValueChange: formValues.useGraphSettings
              ? null
              : formValues.downgradeDefaultValueChange,
          },
        });
      },
    });

  const clearErrorsAndSubmit = useCallback(() => {
    setUpdateConfigError(null);
    submitForm();
  }, [setUpdateConfigError, submitForm]);

  const submitIfDirty = useCallback(() => {
    if (dirty && _.isEmpty(errors)) {
      clearErrorsAndSubmit();
    }
  }, [dirty, errors, clearErrorsAndSubmit]);

  useDebouncedEffect(submitIfDirty, AUTOSAVE_TIMEOUT_MS, [values]);

  return (
    <div className="space-y-4">
      {/* Form update error message */}
      {updateConfigError && <ErrorMessage error={updateConfigError} />}

      <IgnoredConditionsCard
        downgradeStaticChecks={
          values.downgradeStaticChecks ??
          initialValues.downgradeStaticChecks ??
          false
        }
        downgradeDefaultValueChange={
          values.downgradeDefaultValueChange ??
          initialValues.downgradeDefaultValueChange ??
          false
        }
        useGraphSettings={values.useGraphSettings}
        isDisabled={
          !roles.canWriteCheckConfiguration || values.useGraphSettings
        }
        canWriteCheckConfiguration={roles.canWriteCheckConfiguration}
        onChangeDowngradeStaticChecks={(value) =>
          setFieldValue('downgradeStaticChecks', value)
        }
        onChangeDowgradeDefaultValueChange={(value) =>
          setFieldValue('downgradeDefaultValueChange', value)
        }
        onChangeUseGraphSettings={(value) => {
          setFieldValue('useGraphSettings', value);
          if (value === true) {
            setFieldValue('downgradeStaticChecks', undefined);
            setFieldValue('downgradeDefaultValueChange', undefined);
          }
          clearErrorsAndSubmit();
        }}
      />
    </div>
  );
};
